<script setup>
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";
import CountIcon from "@/Components/Profile/CountIcon.vue";

const props = defineProps({
    owner: {
        type: Object,
        required: true,
    },
    // Visitors get the counts row only; the section tabs are owner navigation.
    isOwner: {
        type: Boolean,
        default: false,
    },
    active: {
        type: String,
        default: "lists",
    },
});

const tabs = computed(() => [
    {
        key: "lists",
        label: "My Wish Lists",
        href: route("user.giftlists", { username: props.owner.username }),
    },
    {
        key: "bookmarks",
        label: "Saved Lists",
        href: route("bookmarks"),
    },
    // For Me / By Me are a toggle inside the Confirmed Gifts section (GH-878),
    // so the secondary nav carries a single entry for both views.
    {
        key: "confirmed",
        label: "Confirmed Gifts",
        href: route("gifts.for_you"),
    },
]);

// Counts double as navigation: owners land on their own follower pages,
// visitors on the owner's public ones (GH-851). A member who hides their
// connections keeps the numbers, but visitors get no link to the lists.
const connectionsHidden = computed(
    () => !props.isOwner && props.owner.connections_hidden === true,
);

const counts = computed(() => [
    {
        // "lists" is the section tabs' active key; the count itself never
        // shows an active underline, so it gets a distinct key.
        key: "lists-count",
        icon: "lists",
        value: props.owner.list_count,
        label: "Lists",
        href: route("user.giftlists", { username: props.owner.username }),
    },
    {
        key: "followers",
        icon: "followers",
        value: props.owner.followers_count,
        label: "Followers",
        href: props.isOwner
            ? route("friends.followers")
            : connectionsHidden.value
              ? null
              : route("followers.theirs", { user: props.owner.uuid }),
    },
    {
        key: "following",
        icon: "following",
        value: props.owner.following_count,
        label: "Following",
        href: props.isOwner
            ? route("friends.index")
            : connectionsHidden.value
              ? null
              : route("following.theirs", { user: props.owner.uuid }),
    },
]);

const countIconSizes = {
    lists: "h-3.5 w-4",
    followers: "h-4 w-[22px]",
    following: "h-[18px] w-[21px]",
};
</script>

<template>
    <!-- Mobile (GH-1089): the counts + CTA close out the white container
         (10px bottom radius, matching the card top) and the section tabs sit
         outside it on the beige page. lg keeps the single white bar. -->
    <div
        class="flex flex-col max-lg:bg-brand-cream lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:bg-white lg:px-[46px] lg:py-7"
    >
        <!-- justify-center would clip the leading tab once the row overflows,
             so the scroll row stays start-aligned and bleeds to the edges -->
        <nav
            v-if="isOwner"
            class="flex items-center gap-6 overflow-x-auto whitespace-nowrap font-brand text-[15px] font-semibold [scrollbar-width:none] max-lg:order-3 max-lg:justify-between max-lg:px-5 max-lg:pb-1 max-lg:pt-5 lg:gap-12 [&::-webkit-scrollbar]:hidden"
        >
            <Link
                v-for="tab in tabs"
                :key="tab.key"
                :href="tab.href"
                class="border-b-2 border-solid pb-1.5 text-[#102a3d]"
                :class="
                    active === tab.key
                        ? 'border-brand-red'
                        : 'border-transparent transition hover:text-brand-red'
                "
            >
                {{ tab.label }}
            </Link>
        </nav>

        <!-- White zone: counts + the mobile CTA slot; lg:contents dissolves
             it so the desktop bar keeps its flex layout. -->
        <div
            class="max-lg:flex max-lg:flex-col max-lg:gap-5 max-lg:rounded-b-[10px] max-lg:bg-white max-lg:px-5 max-lg:pb-6 max-lg:pt-1 lg:contents"
        >
            <!-- Mobile slot (e.g. the Create List button between counts and tabs) -->
            <slot />

            <div
                class="flex items-center justify-center gap-7 font-brand text-[15px] text-[#102a3d] max-lg:order-first lg:justify-end lg:gap-10"
                :class="!isOwner && 'lg:mx-auto'"
            >
                <component
                    :is="count.href ? Link : 'span'"
                    v-for="count in counts"
                    :key="count.label"
                    :href="count.href"
                    class="inline-flex items-center gap-2.5 border-b-2 border-solid pb-1"
                    :class="[
                        active === count.key
                            ? 'border-brand-red'
                            : 'border-transparent',
                        count.href &&
                            active !== count.key &&
                            'transition hover:text-brand-red',
                    ]"
                >
                    <CountIcon
                        :icon="count.icon"
                        :class="countIconSizes[count.icon]"
                    />
                    <span
                        ><strong>{{ count.value }}</strong>
                        {{ count.label }}</span
                    >
                </component>
            </div>
        </div>
    </div>
</template>
